/** Dates in the workshop's time zone. `LocalDate` strings ("2026-10-29") are never passed through `Date`. */
export const TZ = 'America/Argentina/Buenos_Aires';

export type LocalDate = string;
export type Instant = string;

const dateTime = new Intl.DateTimeFormat('es-AR', {
  timeZone: TZ,
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});
const dateOnly = new Intl.DateTimeFormat('es-AR', {
  timeZone: TZ,
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});
const isoParts = new Intl.DateTimeFormat('en-CA', {
  timeZone: TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});
const relative = new Intl.RelativeTimeFormat('es-AR', { numeric: 'auto' });
const longDate = new Intl.DateTimeFormat('es-AR', { day: 'numeric', month: 'long', timeZone: 'UTC' });

/** 2026-10-22T17:05:00Z → "22/10/2026 14:05" */
export function formatInstant(value: Instant | null | undefined): string {
  if (!value) return '—';
  return dateTime.format(new Date(value)).replace(',', '');
}

export function formatInstantDate(value: Instant | null | undefined): string {
  if (!value) return '—';
  return dateOnly.format(new Date(value));
}

/** "2026-10-29" → "29/10/2026" */
export function formatLocalDate(value: LocalDate | null | undefined): string {
  if (!value) return '—';
  const [y, m, d] = value.split('-');
  return `${d}/${m}/${y}`;
}

/** "2026-10-29" → "29 de octubre" */
export function formatLocalDateLong(value: LocalDate): string {
  const [y, m, d] = value.split('-').map(Number);
  return longDate.format(new Date(Date.UTC(y!, m! - 1, d!)));
}

/** Today in Buenos Aires as yyyy-MM-dd (what the backend compares validity against). */
export function todayAR(now: Date = new Date()): LocalDate {
  return isoParts.format(now);
}

export function addDays(date: LocalDate, days: number): LocalDate {
  return new Date(utcDay(date) + days * 86_400_000).toISOString().slice(0, 10);
}

/** Whole days from `from` to `to` (both LocalDate). */
export function daysBetween(from: LocalDate, to: LocalDate): number {
  return Math.round((utcDay(to) - utcDay(from)) / 86_400_000);
}

function utcDay(date: LocalDate): number {
  const [y, m, d] = date.split('-').map(Number);
  return Date.UTC(y!, m! - 1, d!);
}

/** "hace 3 h", "en 2 días" */
export function formatRelative(value: Instant | null | undefined, now: Date = new Date()): string {
  if (!value) return '—';
  const seconds = Math.round((new Date(value).getTime() - now.getTime()) / 1000);
  const abs = Math.abs(seconds);
  if (abs < 45) return 'recién';
  if (abs < 3600) return relative.format(Math.round(seconds / 60), 'minute');
  if (abs < 86_400) return relative.format(Math.round(seconds / 3600), 'hour');
  if (abs < 86_400 * 30) return relative.format(Math.round(seconds / 86_400), 'day');
  return formatInstantDate(value);
}

export function hoursSince(value: Instant, now: Date = new Date()): number {
  return (now.getTime() - new Date(value).getTime()) / 3_600_000;
}
